/**
 * videoSeoConfig — catálogo ESTÁTICO de videos públicos indexables.
 *
 * Por qué estático: el build de Railway es DB-free (el builder no llega al
 * Postgres privado — ver CLAUDE.md), así que el sitemap de video y el JSON-LD
 * tienen que salir de config/env, nunca de una query.
 *
 * Regla dura: acá SÓLO van rutas indexables. Cualquier ruta que matchee la
 * regex `NOINDEX_NSFW` / `NOINDEX_TEST` de server/middleware/seoHeaders.ts
 * (/desnuda, /nude, /videos, /reels, /premium, /exclusivo…) NO debe listarse:
 * emitir VideoObject en una página noindex es desperdicio y potencialmente
 * contraproducente.
 *
 * Requisito de Google para rich results de video: name + description +
 * thumbnailUrl + uploadDate son OBLIGATORIOS. Si `uploadDate` no está seteada
 * la entrada queda inactiva (el componente devuelve null y el sitemap la
 * omite) — preferimos no emitir schema antes que inventar una fecha.
 *
 * `uploadDate` y `durationSeconds` NO se definen a mano: los resuelve el
 * prebuild (`scripts/resolve-video-seo-metadata.mjs`) contra el manifiesto HLS
 * público y la API de Mux, y los deja en `videoSeoGenerated.json`. Las env
 * `VITE_BRAND_HERO_VIDEO_*` siguen existiendo sólo como override manual.
 */

import { BRAND_MUX_DEFAULT_PLAYBACK_ID, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import videoSeoGenerated from "@/config/videoSeoGenerated.json";

export interface PublicVideoSeoEntry {
  /** Ruta pública indexable donde vive el video. Ej: "/" */
  path: string;
  /** Mux playback ID (público). */
  playbackId: string;
  title: string;
  description: string;
  /** ISO 8601. Sin esto la entrada queda inactiva (requisito de Google). */
  uploadDate?: string;
  durationSeconds?: number;
}

const env = import.meta.env;

/** Metadatos resueltos por el prebuild contra Mux (ver header del módulo). */
const generated: Record<
  string,
  { uploadDate?: string; durationSeconds?: number }
> = (videoSeoGenerated as { videos?: Record<string, { uploadDate?: string; durationSeconds?: number }> })
  .videos ?? {};

const heroGenerated = generated[BRAND_MUX_DEFAULT_PLAYBACK_ID] ?? {};

// Override manual primero, dato resuelto después. Ninguno es obligatorio.
const HERO_UPLOAD_DATE =
  (env.VITE_BRAND_HERO_VIDEO_UPLOAD_DATE ?? "").trim() ||
  (heroGenerated.uploadDate ?? "");

const HERO_DURATION_OVERRIDE = Number.parseInt(
  (env.VITE_BRAND_HERO_VIDEO_DURATION_SECONDS ?? "").trim(),
  10,
);
const HERO_DURATION = Number.isFinite(HERO_DURATION_OVERRIDE)
  ? HERO_DURATION_OVERRIDE
  : (heroGenerated.durationSeconds ?? Number.NaN);

/**
 * Video hero de la home (`/`). La home es indexable; el playback ID sale de
 * brandConfig así el fork de otra marca no necesita tocar código.
 */
const HERO_ENTRY: PublicVideoSeoEntry = {
  path: "/",
  playbackId: BRAND_MUX_DEFAULT_PLAYBACK_ID,
  title:
    (env.VITE_BRAND_HERO_VIDEO_TITLE ?? "").trim() ||
    `${BRAND_PERSON_NAME} — video de presentación`,
  description:
    (env.VITE_BRAND_HERO_VIDEO_DESCRIPTION ?? "").trim() ||
    `Video de presentación de ${BRAND_PERSON_NAME}, creadora de contenido.`,
  uploadDate: HERO_UPLOAD_DATE || undefined,
  durationSeconds: Number.isFinite(HERO_DURATION) && HERO_DURATION > 0 ? HERO_DURATION : undefined,
};

/** Entradas activas = las que tienen todos los campos obligatorios de Google. */
export const PUBLIC_VIDEO_SEO_ENTRIES: PublicVideoSeoEntry[] = [HERO_ENTRY].filter(
  e => Boolean(e.playbackId && e.title && e.description && e.uploadDate),
);

/** Entrada indexable para una ruta dada (o null). */
export function getVideoSeoEntryForPath(pathname: string): PublicVideoSeoEntry | null {
  const p = pathname.replace(/\/+$/, "") || "/";
  return PUBLIC_VIDEO_SEO_ENTRIES.find(e => e.path === p) ?? null;
}
